import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'

/**
 * The keys that read as a lead-in rather than as a capability — "everything in
 * FaceWise, plus:". They carry no tick, because a tick beside them would count
 * a sentence as a feature.
 */
const LEAD_IN_PREFIX = 'everything_in_'

/**
 * What a plan contains, as a list of things rather than a list of adjectives.
 *
 * The keys come from the API and are translated here; the frontend never
 * invents an entry, so a capability that has not shipped cannot appear on the
 * price list by accident.
 *
 * A key with no translation is dropped rather than printed. The two sides
 * deploy separately, so a backend that is one release ahead will send a key
 * this build has never heard of, and printing `one_measurement` at a customer
 * is worse than showing one bullet fewer.
 */
export default function IncludesList({ includes }: { includes: string[] }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')

  return (
    <Stack component="ul" spacing={1.1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {includes.map((key) => {
        const label = t(`includes.${key}`, { defaultValue: '' })
        if (!label) return null
        const isLeadIn = key.startsWith(LEAD_IN_PREFIX)
        return (
          <Box
            component="li"
            key={key}
            sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
          >
            {isLeadIn ? (
              <Box sx={{ width: 16, flexShrink: 0 }} />
            ) : (
              <CheckIcon sx={{ fontSize: 16, color: c.coral, mt: '2px', flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                fontSize: 13.5,
                lineHeight: 1.45,
                color: isLeadIn ? c.ink : c.ink72,
                fontWeight: isLeadIn ? 700 : 400,
              }}
            >
              {label}
            </Typography>
          </Box>
        )
      })}
    </Stack>
  )
}
